/**
 * Field-mapping GPS quality — uses browser-reported horizontal accuracy (95% radius, meters).
 * Not survey-grade; used to hint mappers and drop obviously poor trace vertices.
 */

/** Skip trace vertices when reported accuracy exceeds this (meters). */
export const FIELD_MAPPING_MAX_GPS_ACCURACY_M = 15;

/** How long “Add corner” waits for a fix at or below {@link FIELD_MAPPING_MAX_GPS_ACCURACY_M}. */
export const FIELD_MAPPING_CORNER_WAIT_MS = 25_000;

/** Accuracy at or below this is shown as "good" in the live pill. */
export const FIELD_MAPPING_GOOD_GPS_ACCURACY_M = 10;

/** Smart-trace distance presets (meters between accepted vertices). */
export const TRACE_DISTANCE_OPTIONS_M = [3, 5, 8, 10, 15] as const;

export type TraceDistanceOptionM = (typeof TRACE_DISTANCE_OPTIONS_M)[number];

export const DEFAULT_TRACE_MIN_DISTANCE_M: TraceDistanceOptionM = 5;

export type TraceSamplingMode = 'distance' | 'always';

/** Continuous GPS watch vs manual corner capture at each stop. */
export type TraceCaptureMode = 'continuous' | 'corners';

export type FieldMappingGpsQuality = 'good' | 'fair' | 'poor' | 'unknown';

export function fieldMappingGpsQuality(
  accuracyM: number | null | undefined,
): FieldMappingGpsQuality {
  if (accuracyM == null || !Number.isFinite(accuracyM) || accuracyM <= 0) {
    return 'unknown';
  }
  if (accuracyM <= FIELD_MAPPING_GOOD_GPS_ACCURACY_M) return 'good';
  if (accuracyM <= FIELD_MAPPING_MAX_GPS_ACCURACY_M) return 'fair';
  return 'poor';
}

/** Accept fix for trace vertices; unknown accuracy is kept (many browsers omit it). */
export function isGpsAccuracyAcceptableForFieldMapping(
  accuracyM: number | null | undefined,
): boolean {
  if (accuracyM == null || !Number.isFinite(accuracyM)) return true;
  return accuracyM <= FIELD_MAPPING_MAX_GPS_ACCURACY_M;
}

/** Haversine distance in metres between two lat/lng points. */
export function haversineMetres(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Whether to append a trace vertex given sampling mode and last accepted point. */
export function shouldAcceptTracePoint(
  newPoint: { lat: number; lng: number },
  lastAccepted: { lat: number; lng: number } | null,
  mode: TraceSamplingMode,
  minDistanceM: number,
): boolean {
  if (!lastAccepted) return true;
  if (mode === 'always') return true;
  return (
    haversineMetres(newPoint.lat, newPoint.lng, lastAccepted.lat, lastAccepted.lng) >= minDistanceM
  );
}
